import { useNewsCategoryLabels } from '@/context/newsCategoryLabelsContext';
import { getNewsCategoryLabel } from '@/util/storyblok/news-categories';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import type { ReactElement, RefObject, Ref } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './NewsOverviewItem.module.scss';
import { onHover, scaleImage, setupTransitionInTimeline } from './NewsOverviewItem.transitions';
import type { NewsOverviewItemProps, NewsOverviewItemRef } from './NewsOverviewItem.data';
import { useScrollTransition } from 'util/react-transition-component';
import Image from '../../../atoms/image/Image';
import { cleanMediaQueries } from '../../../../hooks/useDeviceStateTracker';
import Heading from '../../../atoms/heading/Heading';
import Text from '../../../atoms/text/Text';
import { protocolRelativeToAbsoluteUrl } from '../../../../util/url.utils';
import useIsomorphicLayoutEffect from '../../../../hooks/useIsomorphicLayoutEffect';
import { formatDate } from '../../../../util/dates.utils';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import { renderCta } from '../../../../util/cta/cta.template';
import type { CtaRefType } from '../../../../util/cta/cta.data';
import { isTouch } from '../../../../hooks/useIsDesktopDevice';
import PrimaryLink from '../../../atoms/primary-link/PrimaryLink';
import type { PrimaryLinkRef } from '../../../atoms/primary-link/PrimaryLink.data';
import {
  addImageInVideoObject,
  getFallbackVideo,
  hasVideo,
} from '../../../../util/storyblok/data.utils';
import Video from '../../../atoms/video/Video';
import { useGlobalStore } from '../../../../stores/global';
import useUnmount from '../../../../hooks/useUnmount';
import { useCanPlayWebmContext } from '../../../../context/canPlayWebmContext';

export default function NewsOverviewItem({
  blok,
  full_slug,
  textOnly,
  transitionInDelay,
  ref,
  ...props
}: NewsOverviewItemProps & { ref?: Ref<NewsOverviewItemRef> }): ReactElement {
  const { locale } = useRouter();
  const categoryLabels = useNewsCategoryLabels();
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const buttonRef = useRef<CtaRefType>(null);

  const imageHolderRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLSpanElement>(null);
  const [formattedDate, setFormattedDate] = useState<string | undefined | null>(
    formatDate(blok?.publish_date, 'D MMM YYYY', locale),
  );
  const categoryRef = useRef<HeadingRef>(null);
  const titleRef = useRef<HeadingRef>(null);
  const dateRef = useRef<HeadingRef>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const gifRef = useRef<HTMLVideoElement>(null);
  const downloadLinkRef = useRef<PrimaryLinkRef>(null);
  const backgroundHoverRef = useRef<HTMLDivElement>(null);
  const globalStore = useGlobalStore();
  const [isHover, setIsHover] = useState(false);
  const isDownloadHover = useRef(false);
  const mouseLeaveTimeOut = useRef<NodeJS.Timeout>(undefined);
  const { canPlayWebm } = useCanPlayWebmContext();

  const { inView } = useScrollTransition({
    scroll: {
      trigger: elementRef,
      transitionInDelay,
      vars: {},
      enableInView: true,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        imageHolderRef,
        lineRef,
        categoryRef,
        titleRef,
        dateRef,
        imageRef,
        downloadLinkRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const { key_image, key_video, thumbnail_video } = blok;

  let src;
  let srcDesktop;
  if (key_image && key_image?.length > 0) {
    src = protocolRelativeToAbsoluteUrl(key_image[0].image_portrait);
    srcDesktop = protocolRelativeToAbsoluteUrl(key_image[0].image_landscape);

    if (!src) {
      src = protocolRelativeToAbsoluteUrl(key_image[0].image_landscape);
    }
  }

  useUnmount(() => {
    if (mouseLeaveTimeOut?.current) {
      clearTimeout(mouseLeaveTimeOut.current);
    }
  });

  useIsomorphicLayoutEffect(() => {
    if (blok?.publish_date) {
      setFormattedDate(formatDate(blok?.publish_date, 'D MMM YYYY', locale));
    }
  }, [blok.publish_date, locale]);

  const onMouseEnter = useCallback(() => {
    setIsHover(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    mouseLeaveTimeOut.current = setTimeout(() => {
      if (!isDownloadHover.current) {
        setIsHover(false);
      }
    }, 0);
  }, []);

  const onDownloadMouseEnter = useCallback(() => {
    isDownloadHover.current = true;
  }, []);

  const onDownloadMouseLeave = useCallback(() => {
    isDownloadHover.current = false;
  }, []);

  useEffect(() => {
    onHover(backgroundHoverRef, isHover);
    scaleImage(imageRef, isHover);
  }, [isHover]);

  let media = <></>;

  if ((key_video && hasVideo(key_video)) || (thumbnail_video && hasVideo(thumbnail_video))) {
    media = (
      <Video
        blok={addImageInVideoObject(
          getFallbackVideo(thumbnail_video, key_video),
          srcDesktop || src,
        )}
        objectFit="cover"
        controls={false}
        playButton={false}
        muted
        loop
        openInModal={false}
        playing={inView}
        className={classNames(styles.video, 'abs-fill')}
      />
    );
  } else if (key_image?.[0]?.animated_image?.filename && canPlayWebm) {
    media = (
      <video
        ref={gifRef}
        src={key_image?.[0]?.animated_image.filename}
        autoPlay
        muted
        controls={false}
        playsInline
        loop
        className={classNames(styles.animatedImage)}
      />
    );
  } else if (key_image?.[0] && src && !hasVideo(key_video) && !hasVideo(thumbnail_video)) {
    media = (
      <Image
        ref={imageRef}
        src={src}
        srcDesktop={srcDesktop || src}
        layout="fill"
        className={classNames(styles.image, 'abs-fill')}
        sizes={`${cleanMediaQueries.MIN_768} 1200px, ${cleanMediaQueries.MIN_1920} 1680px, 100vw`}
        alt={key_image[0].alt_text}
      />
    );
  }

  return (
    <div
      ref={elementRef}
      className={classNames(styles.newsOverviewItem, props.className, {
        [styles.isHighlight]: blok.category === 'Thought Leadership',
      })}
    >
      {renderCta({
        elementRef: buttonRef,
        blok,
        props: {
          link: {
            href: full_slug,
            uuid: blok._uid,
          },
          className: classNames(styles.button),
          onMouseEnter,
          onMouseLeave,
        },
        innerTemplate: (
          <>
            {!textOnly && (
              <div ref={imageHolderRef} className={styles.imageHolder}>
                {media}
              </div>
            )}

            <div className={classNames(styles.content)}>
              <div
                ref={backgroundHoverRef}
                className={classNames(styles.backgroundHover, 'abs-fill')}
              />
              <Heading
                ref={categoryRef}
                transitionInType={isTouch ? 'none' : 'splitWordsSlideInUp'}
                transitionOutType={isTouch ? 'none' : 'splitWordsSlideOutUp'}
                className={classNames(styles.category)}
                as="h3"
                size="label-small"
              >
                {getNewsCategoryLabel(categoryLabels, blok.category)}
              </Heading>

              <Heading
                ref={titleRef}
                transitionInType={isTouch ? 'none' : 'splitWordsSlideInUp'}
                transitionOutType={isTouch ? 'none' : 'splitWordsSlideOutUp'}
                className={classNames(styles.title)}
                as="h2"
                size="quote-small"
              >
                {blok.title}
              </Heading>

              <Text
                ref={dateRef}
                transitionInType={isTouch ? 'none' : 'splitWordsSlideInUp'}
                transitionOutType={isTouch ? 'none' : 'splitWordsSlideOutUp'}
                className={classNames(styles.date)}
                as="time"
                size="body-standard"
              >
                {formattedDate}
              </Text>
              <span ref={lineRef} className={classNames(styles.line)}></span>
            </div>
          </>
        ),
      })}

      {!!blok.pardot_id && (
        <PrimaryLink
          ref={downloadLinkRef}
          textVariant="label-small"
          className={classNames(styles.downloadLink)}
          link={{ href: `${full_slug}#download-pdf` }}
          label={
            blok?.pardot_download_label || globalStore.globalSettings.form_pardot_pdf_download_label
          }
          transitionInType={isTouch ? 'none' : 'splitWordsSlideInUp'}
          icon="download"
          onMouseEnter={onDownloadMouseEnter}
          onMouseLeave={onDownloadMouseLeave}
        />
      )}
    </div>
  );
}
